import type {
  Broker,
  BranchKind,
  CreditLimitGroup,
  InsuredAddress,
  Modality,
  OpeningObject,
  PolicyholderCreditLine,
  Product,
  QuoteModalityLimits,
  StageSLA,
} from '@/shared/api/resources';
import type { Money } from '@/shared/api/types';
import {
  formatMoney,
  formatRate,
  moneyToInput,
  rateToInput,
} from '@/shared/lib/format';

import { gruposDeModalidade } from '../policyholders/rotulos';

/**
 * O que a Nova operacao ja sabe (ADR 0103).
 *
 * Cada campo sugere um valor e diz de onde ele veio. Valor vazio e "nao ha o que
 * sugerir", e a origem diz por que: nada aqui inventa taxa, prazo ou valor. Sao
 * funcoes puras: quem digita no campo passa por cima, e o formulario decide isso.
 */
export interface Sugestao {
  valor: string;
  origem: string;
}

const emBranco = (origem: string): Sugestao => ({ valor: '', origem });

/** Etapa em que a operacao da seguradora nasce: "aguardando_credito" (stage_slas). */
const ETAPA_DA_ABERTURA = 'aguardando_credito';

/** O texto livre do fim do objeto e opcional. */
const TAG_DO_TEXTO_LIVRE = 'objeto.texto_corretor';

/** Tipo da TAG que a plataforma preenche. */
const TAG_AUTOMATICA = 'automatica';

/** A corretora nao ve limite nem taxa enquanto a analise esta aberta (PM-09). */
const OCULTO_NA_ABERTURA = 'Limite e taxa ocultos neste momento: informe o valor.';

function nomeDoGrupo(grupo: string): string {
  return gruposDeModalidade[grupo] ?? grupo;
}

/** Grupo de credito da modalidade; sem ele na modalidade, o do grupo do ramo. */
export function grupoDeCreditoDaModalidade(
  produto: Product | undefined,
  modalidade: Modality | undefined,
): string {
  if (!modalidade) return '';
  if (modalidade.credit_group) return modalidade.credit_group;
  const grupoDoRamo = produto?.modality_groups?.find(
    (grupo) => grupo.id === modalidade.modality_group_id,
  );
  return grupoDoRamo?.credit_group ?? '';
}

/**
 * O limite que a analise deixou no grupo.
 *
 * O limite gravado por uma pessoa e o ajuste, e vale mesmo em zero: quem zerou nao
 * aprovou. Sem ajuste, vale o aprovado da analise automatica e, enquanto ele nao
 * existir, o limite do motor (`RecordEngineLimit`, ADR 0035).
 *
 * `changed_manually` nao diz quem aprovou: a pessoa que muda so a taxa de um limite
 * aprovado pelo motor liga a marca, e o motor pode aprovar numa linha que ja tinha taxa
 * digitada. Quem diz e `approved_origin`; sem ele, o limite de antes da marca do motor,
 * que so uma pessoa gravava. O limite so calculado pelo motor nao e concedido.
 */
function limiteDaAnalise(limite: CreditLimitGroup): {
  valor: Money;
  ajustado: boolean;
} | null {
  if (!limite.configured || limite.approved.amount <= 0) return null;
  const ajustado = limite.approved_origin
    ? limite.approved_origin === 'manual'
    : limite.changed_manually;
  return { valor: limite.approved, ajustado };
}

/**
 * Valor garantido: o disponivel do limite do tomador no grupo da modalidade.
 *
 * O disponivel e o do limite calculado, quando a dimensao do grupo e "calculavel"; sem
 * ele, o limite menos o utilizado informado em Limites e Taxas. Limite vencido, oculto
 * ou sem saldo nao vira valor: o campo fica em branco e diz o motivo. A corretora nao ve
 * quem aprovou, e a origem nao conta para ela.
 */
export function sugerirValorGarantido(
  grupo: string,
  limites: CreditLimitGroup[],
  linha: PolicyholderCreditLine | undefined,
  daCorretora = false,
): Sugestao {
  if (!grupo)
    return emBranco('A modalidade nao tem grupo de credito: informe o valor.');
  const nome = nomeDoGrupo(grupo);
  const semLimite = emBranco(`Sem limite aprovado no grupo ${nome}: informe o valor.`);

  const limite = limites.find((item) => item.modality_group === grupo);
  if (!limite) return semLimite;
  if (limite.hidden_from_broker) return emBranco(OCULTO_NA_ABERTURA);
  const daAnalise = limiteDaAnalise(limite);
  if (!daAnalise) {
    return limite.engine_limit && limite.engine_limit.amount > 0
      ? emBranco(
          `Sem limite aprovado no grupo ${nome}: o motor calculou ${formatMoney(limite.engine_limit)}, sem aprovacao. Informe o valor.`,
        )
      : semLimite;
  }

  const dimensao = linha?.lines
    .find((item) => item.modality_group === grupo)
    ?.dimensions.find((item) => item.dimension === 'grupo_de_modalidade');
  if (dimensao?.validity === 'vencido') {
    return emBranco(`Limite do grupo ${nome} vencido: informe o valor.`);
  }

  const calculado = dimensao?.situation === 'calculavel' ? dimensao.available : null;
  const disponivel: Money = calculado ?? {
    ...daAnalise.valor,
    amount: Math.max(daAnalise.valor.amount - limite.used.amount, 0),
  };
  if (disponivel.amount <= 0) {
    return emBranco(`Limite do grupo ${nome} todo utilizado: informe o valor.`);
  }

  const fonte = daCorretora
    ? `limite aprovado em Limites e Taxas, grupo ${nome}`
    : daAnalise.ajustado
      ? `limite ajustado em Limites e Taxas, grupo ${nome}`
      : `limite aprovado pela analise automatica, grupo ${nome}`;
  const inteiro = disponivel.amount === daAnalise.valor.amount;
  return {
    valor: moneyToInput(disponivel),
    origem: inteiro
      ? `Sugerido: ${fonte}.`
      : `Sugerido: disponivel do ${fonte} (aprovado ${formatMoney(daAnalise.valor)}).`,
  };
}

/**
 * Taxa aplicada: a do tomador em Limites e Taxas, publica ou privada pelo tipo do ramo.
 *
 * Sem ela, a taxa base da modalidade, quando a configuracao usa taxa por modalidade
 * (ADR 0091). Fora disso a taxa fica em branco e e definida na analise: taxa zero nunca
 * vira sugestao.
 */
export function sugerirTaxa(
  grupo: string,
  limites: CreditLimitGroup[] | undefined,
  ramo: BranchKind | null | undefined,
  modalidade: Modality | undefined,
): Sugestao {
  const limite = limites?.find((item) => item.modality_group === grupo);
  const taxa =
    ramo === 'setor_publico'
      ? limite?.public_rate_bps
      : ramo === 'setor_privado'
        ? limite?.private_rate_bps
        : null;
  if (taxa && taxa > 0) {
    const setor = ramo === 'setor_publico' ? 'publico' : 'privado';
    return {
      valor: rateToInput(taxa),
      origem: `Sugerido: taxa do tomador para o setor ${setor} em Limites e Taxas, grupo ${nomeDoGrupo(grupo)}.`,
    };
  }
  if (modalidade?.uses_modality_rate === true && modalidade.base_rate_bps > 0) {
    return {
      valor: rateToInput(modalidade.base_rate_bps),
      origem: 'Sugerido: taxa base da modalidade.',
    };
  }
  if (limites === undefined || !grupo) return emBranco('');
  if (limite?.hidden_from_broker) return emBranco(OCULTO_NA_ABERTURA);
  if (!ramo) {
    return emBranco('O ramo nao diz se o setor e publico ou privado: informe a taxa.');
  }
  return emBranco(
    `Sem taxa do tomador no grupo ${nomeDoGrupo(grupo)}: informe a taxa ou deixe para a analise.`,
  );
}

/** Corretor do tomador: so o ativo, porque a operacao nova nao aceita outro. */
export function sugerirCorretor(corretor: Broker): Sugestao {
  if (corretor.stage === 'ativo') {
    return { valor: corretor.id, origem: 'Sugerido: corretor do tomador.' };
  }
  return emBranco(`O corretor do tomador (${corretor.legal_name}) nao esta ativo.`);
}

/**
 * Comissao: o percentual cadastrado na corretora e, sem ele, a comissao padrao da empresa,
 * de Configuracao da cotacao (decisao do dono de 03/11/2026, OPEN_QUESTIONS 2.97).
 *
 * A sugestao nunca passa do limite de comissao da corretora, quando ela tem limite:
 * passando, sugere o limite e diz por que. E a mesma regra na Nova operacao e no
 * assistente da corretora. O campo continua editavel, e o digitado prevalece.
 *
 * Sem a comissao padrao (a leitura das opcoes falhou), a corretora sem percentual deixa o
 * campo em branco, como antes: nada aqui inventa percentual.
 */
export function sugerirComissao(
  corretor: Pick<Broker, 'commission_bps' | 'max_commission_bps'>,
  padraoDaEmpresa?: number,
): Sugestao {
  let base: number;
  let origem: string;
  let fonte: string;
  if (corretor.commission_bps > 0) {
    base = corretor.commission_bps;
    origem = 'Sugerido: percentual cadastrado na corretora.';
    fonte = 'O percentual cadastrado na corretora';
  } else if (padraoDaEmpresa !== undefined) {
    base = padraoDaEmpresa;
    origem = 'Sugerido: comissao padrao da empresa, em Configuracao da cotacao.';
    fonte = 'A comissao padrao da empresa';
  } else {
    return emBranco('A corretora nao tem percentual de comissao cadastrado.');
  }

  const limite = corretor.max_commission_bps;
  if (limite !== undefined && limite !== null && base > limite) {
    return {
      valor: rateToInput(limite),
      origem: `Sugerido: limite de comissao da corretora. ${fonte} (${formatRate(base)}) passa do limite.`,
    };
  }
  return { valor: rateToInput(base), origem };
}

/** O produto em que uma operacao nova pode nascer: ativo e com modalidade ativa. */
export function produtosPossiveis(lista: Product[]): Product[] {
  return lista.filter(
    (produto) =>
      produto.status === 'ativo' &&
      produto.modalities.some((modalidade) => modalidade.status === 'ativo'),
  );
}

/** Pre-seleciona so quando ha uma opcao possivel. */
export function sugerirOpcaoUnica(
  opcoes: { id: string }[],
  origem: string,
): Sugestao | undefined {
  const [unica, ...resto] = opcoes;
  return unica && resto.length === 0 ? { valor: unica.id, origem } : undefined;
}

/** Endereco do segurado: o unico ativo ou o principal. */
export function sugerirEndereco(enderecos: InsuredAddress[]): Sugestao | undefined {
  const ativos = enderecos.filter((endereco) => endereco.active);
  const [unico, ...resto] = ativos;
  if (unico && resto.length === 0) {
    return { valor: unico.id, origem: 'Sugerido: unico endereco do segurado.' };
  }
  const principal = ativos.find((endereco) => endereco.is_main);
  return principal
    ? { valor: principal.id, origem: 'Sugerido: endereco principal do segurado.' }
    : undefined;
}

/**
 * Soma dias uteis a uma data AAAA-MM-DD, contando do dia seguinte e pulando sabado e
 * domingo, como `civil.DueInBusinessDays` faz no servidor.
 */
export function somarDiasUteis(data: string, dias: number): string {
  const dia = new Date(`${data}T12:00:00Z`);
  for (let contados = 0; contados < dias;) {
    dia.setUTCDate(dia.getUTCDate() + 1);
    if (dia.getUTCDay() !== 0 && dia.getUTCDay() !== 6) contados += 1;
  }
  return dia.toISOString().slice(0, 10);
}

/** Prazo da etapa: o prazo por etapa da empresa (stage_slas), quando ha. */
export function sugerirPrazoDaEtapa(
  prazos: StageSLA[],
  hoje: string,
): Sugestao | undefined {
  const dias = prazos.find((prazo) => prazo.stage === ETAPA_DA_ABERTURA)?.business_days;
  if (!dias || dias <= 0) return undefined;
  return {
    valor: somarDiasUteis(hoje, dias),
    origem: `Sugerido: prazo da empresa para a analise de credito, ${dias} ${
      dias === 1 ? 'dia util' : 'dias uteis'
    }.`,
  };
}

/** Texto corrido do corpo saneado, um paragrafo por linha. */
function textoDoCorpo(corpo: string): string {
  const documento = new DOMParser().parseFromString(
    corpo.replace(/<br\s*\/?>/gi, '\n').replace(/<\/(p|div|h[1-6]|li)>/gi, '$&\n'),
    'text/html',
  );
  return (documento.body.textContent ?? '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim();
}

/** Campo do objeto que a Nova operacao pede. */
export interface CampoDoObjeto {
  chave: string;
  rotulo: string;
  tipo: string;
  obrigatorio: boolean;
}

/**
 * Os campos do objeto: uma entrada por TAG, com o rotulo do cadastro. Toda TAG e
 * obrigatoria, menos o texto livre, como confere o servidor (`OpeningMissingTag`). A TAG
 * automatica a plataforma preenche, e nao e pedida.
 */
export function camposDoObjeto(objeto: OpeningObject): CampoDoObjeto[] {
  return objeto.tags
    .filter((tag) => tag.input_kind !== TAG_AUTOMATICA)
    .map((tag) => ({
      chave: tag.tag_key,
      rotulo: tag.label,
      tipo: tag.input_kind,
      obrigatorio: tag.tag_key !== TAG_DO_TEXTO_LIVRE,
    }));
}

/**
 * O texto do objeto com o que ja se sabe: o digitado nas TAGs e, nos dados do cadastro,
 * o que foi escolhido no formulario. O que falta aparece entre colchetes, com o rotulo.
 * E previa: quem preenche de verdade e o servidor, ao gerar a minuta.
 */
export function previaDoObjeto(
  objeto: OpeningObject,
  valores: Record<string, string>,
  conhecidos: Record<string, string>,
): string {
  return textoDoCorpo(objeto.body)
    .replace(/\{\{\s*([a-z_.]+)\s*\}\}/g, (_, chave: string) => {
      const digitado = (valores[chave] ?? conhecidos[chave] ?? '').trim();
      if (digitado) return digitado;
      if (chave === TAG_DO_TEXTO_LIVRE) return '';
      const tag = objeto.tags.find((item) => item.tag_key === chave);
      return `[${tag?.label ?? chave.replace(/[._]/g, ' ')}]`;
    })
    .replace(/\n{2,}/g, '\n')
    .trim();
}

function diasEntre(de: string, ate: string): number {
  const dia = 86_400_000;
  return (Date.parse(`${ate}T00:00:00Z`) - Date.parse(`${de}T00:00:00Z`)) / dia;
}

/** A tratativa da modalidade leva a retroatividade para a esteira, e nao a recusa. */
function retroatividadeVaiParaEsteira(limites: QuoteModalityLimits): boolean {
  return (
    limites.retroactive_pipe_enabled ||
    limites.retroactivity_handling === 'esteira' ||
    limites.retroactivity_handling === 'hibrido'
  );
}

/** Os limites do inicio, para mostrar junto do campo. Vazio sem limite cadastrado. */
export function limitesDoInicio(limites: QuoteModalityLimits | undefined): string {
  const partes = [
    limites?.max_retroactive_days != null
      ? `ate ${limites.max_retroactive_days} dias retroativos`
      : '',
    limites?.max_future_days != null
      ? `ate ${limites.max_future_days} dias futuros`
      : '',
  ].filter(Boolean);
  if (partes.length === 0) return '';
  const texto = partes.join(' e ');
  return `${texto.charAt(0).toUpperCase()}${texto.slice(1)} nesta modalidade.`;
}

/** Os limites da vigencia, para mostrar junto do fim. Vazio sem limite cadastrado. */
export function limitesDoFim(limites: QuoteModalityLimits | undefined): string {
  const minimo = limites?.min_term_days;
  const maximo = limites?.max_term_days;
  if (minimo != null && maximo != null) {
    return `Vigencia de ${minimo} a ${maximo} dias nesta modalidade.`;
  }
  if (minimo != null) return `Vigencia minima de ${minimo} dias nesta modalidade.`;
  if (maximo != null) return `Vigencia maxima de ${maximo} dias nesta modalidade.`;
  return '';
}

/**
 * Confere a vigencia com os limites da modalidade (SUB-004, SUB-009 e SUB-010).
 *
 * As regras seguem pendentes no servidor, que so avisa: a trava vale na Nova operacao,
 * por pedido do dono (ADR 0103). A contagem e a diferenca entre as datas, sem o dia
 * inicial: a convencao nao esta escrita (PRC-008). Limite nulo e nao informado, e nao
 * trava nada. A retroatividade que a modalidade manda para a esteira nao e recusada.
 */
export function conferirVigencia(
  limites: QuoteModalityLimits | undefined,
  inicio: string,
  fim: string,
  hoje: string,
): { inicio: string | undefined; fim: string | undefined } {
  const problemas: { inicio: string | undefined; fim: string | undefined } = {
    inicio: undefined,
    fim: undefined,
  };
  if (inicio && fim) {
    const dias = diasEntre(inicio, fim);
    if (dias < 0) {
      problemas.fim = 'O fim da vigencia nao pode anteceder o inicio.';
    } else if (limites?.min_term_days != null && dias < limites.min_term_days) {
      problemas.fim = `A vigencia minima desta modalidade e de ${limites.min_term_days} dias.`;
    } else if (limites?.max_term_days != null && dias > limites.max_term_days) {
      problemas.fim = `A vigencia maxima desta modalidade e de ${limites.max_term_days} dias.`;
    }
  }
  if (limites && inicio) {
    const futuro = diasEntre(hoje, inicio);
    if (limites.max_future_days != null && futuro > limites.max_future_days) {
      problemas.inicio = `O inicio passa do maximo de ${limites.max_future_days} dias futuros desta modalidade.`;
    } else if (
      limites.max_retroactive_days != null &&
      -futuro > limites.max_retroactive_days &&
      !retroatividadeVaiParaEsteira(limites)
    ) {
      problemas.inicio = `O inicio passa do maximo de ${limites.max_retroactive_days} dias retroativos desta modalidade.`;
    }
  }
  return problemas;
}
